import {
  BodyMedium,
  BodySmall,
  CallingCodeInput,
  Checkbox,
  FormSelect,
  HeadingLarge,
  HeadingMedium,
  SubtleButton,
  ToggleButton,
  Tooltip,
  createPhoneNumberWithCallingCode,
  theme,
  useToast,
  DeprecatedErrorWarningIcon,
} from '@arnold/common';
import { ProductCode } from '@arnold/core';
import styled from '@emotion/styled/macro';
import { format } from 'date-fns';
import { Field, Form, Formik, FormikHelpers, FormikValues } from 'formik';
import { equals, isEmpty, omit, uniqBy } from 'ramda';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, FormControl, FormGroup, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { ExternalPerson, TeamioIcon } from '../../../assets/images';
import {
  ContactInput,
  ContactSource,
  ContactType,
  CreateRespondentMutationFn,
  Features,
  OrganizationQuery,
  OrganizationTeamsQuery,
  ReportRecipientsQuery,
  RespondentInput,
  SurveyGroupsQuery,
  SurveysQuery,
  UpdateRespondentInput,
  UpdateRespondentMutationFn,
  useDeleteRespondentMutation,
  useVerifyPhoneNumbersMutation,
} from '../../../generated/hooks';
import { getOrganizationTeams } from '../../../graphql/queries';
import {
  getCreditPriceForSurveyGroup,
  ONE_TIME_PULSER_SURVEY_CREDIT_PRICE,
  ONE_TIME_SURVEY_CREDIT_PRICE,
} from '../../../lib/creditPricing';
import { sortLanguages } from '../../../lib/common';
import ee from '../../../lib/eventEmitter';
import { useFeatureFlag } from '../../../lib/helpers/hooks';
import { isPulserTopicGroup } from '../../../lib/helpers/pulser';
import { getTeams } from '../../../lib/helpers/teams';
import { findTeamById, SupervisorTeamSelect, TeamWithSubTeams } from '../../../screens/Teams/SupervisorTeamSelect';
import { MemberWithLeaderOfTeams } from '../../../screens/Teams/TeamsAndPeople';
import { constructTeamStructure } from '../../../screens/Teams/TeamsScreen';
import { FormErrorFeedback, FormGroupLabel, FormLabelStyled, FormRadioInput } from '../../Common';
import { SurveyGroupSurveysData } from '../../Modals/AddSGRespondentToOngoingSurveys';
import OnboardingSelect from './OnboardingSelect';
import ProcessSurveySelect, { SurveyGroupEntries } from './ProcessSurveySelect';
import ReportsSection from './ReportsSection';
import StandaloneSurveySelect from './StandaloneSurveySelect';
import { NO_SUPERVISOR, TeamLeaderField } from './TeamLeaderField';
import { getRootTeamsOnly, SELECT_ALL_ID } from './teamSelectionUtils';
import { createValidationSchema, handlePhoneBlur, handlePhoneChange } from './utils';

const ToggleContainerWithText = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing.f} 0;
  &:not(:last-child) {
    border-bottom: ${theme.spacing.a} solid ${theme.colors.borderSeparator.default};
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: self-end;
`;

const RadioButtonError = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: -1.5rem;

  & .invalid-feedback {
    display: block;
  }
`;

type Organization = NonNullable<OrganizationQuery['organization']>;
type Team = NonNullable<OrganizationTeamsQuery['organizationTeams']>[0];
type Member = Team['members'][0];

type FormProps = {
  respondent?: MemberWithLeaderOfTeams;
  workEmail?: string;
  team?: Team;
  organizationTeams: Team[];
  personalEmail?: string;
  phone?: string;
  countryCallingCode?: string;
  preferredContactType?: string;
  teamId?: number;
  language?: string;
  onboardings?: NonNullable<SurveyGroupsQuery['organization']>['surveyGroups'];
  processSurveys?: NonNullable<SurveyGroupsQuery['organization']>['surveyGroups'];
  reportRecipients?: NonNullable<ReportRecipientsQuery['reportRecipients']>;
  disabled?: boolean;
  saveRespondent: UpdateRespondentMutationFn | CreateRespondentMutationFn;
  organization: Organization;
  surveys?: NonNullable<SurveysQuery['user']['organization']>['surveys'];
  formStatus?: RespondentFormStatus;
  setFormStatus?: (status: RespondentFormStatus) => void;
  onDeletionComplete?: () => void;
  setUpdatedReportTeamIds?: (reportTeamIds: string[]) => void;
  onCreateTeamSelected?: (externallySetTeamId: (teamId: string) => void) => void;
  disableCreatingTeam?: boolean;
};

export type RespondentFormStatus = {
  somethingChanged: boolean;
  saveDisabled: boolean;
  save: () => void;
};

const RespondentForm = ({
  workEmail,
  personalEmail,
  phone,
  countryCallingCode,
  saveRespondent,
  respondent,
  team,
  organizationTeams,
  organization,
  onboardings,
  surveys,
  processSurveys,
  reportRecipients,
  formStatus,
  setFormStatus,
  onDeletionComplete,
  setUpdatedReportTeamIds,
  onCreateTeamSelected,
  disableCreatingTeam,
}: FormProps) => {
  const { addToast } = useToast();
  const { t } = useTranslation(['updateRespondent', 'addRespondent']);
  const [unverifiedPhoneNumber, setUnverifiedPhoneNumber] = useState(false);
  const [standaloneSurveysToAdd, setStandaloneSurveysToAdd] = useState<string[]>([]);
  const [processSurveysToAdd, setProcessSurveysToAdd] = useState<SurveyGroupEntries>({});

  const otherRespondentEmails = useMemo(() => {
    return organizationTeams
      .flatMap((t) => t.members)
      .filter((m) => m.id !== respondent?.id)
      .flatMap((m) => m.contacts)
      .filter((c) => c && c.type === ContactType.Email)
      .map((c) => c!.value);
  }, [organizationTeams, respondent]);

  const otherRespondentPhoneNumbers = useMemo(() => {
    return organizationTeams
      .flatMap((t) => t.members)
      .filter((m) => m.id !== respondent?.id)
      .flatMap((m) => m.contacts)
      .filter((c) => c && c.type === ContactType.Sms)
      .map((c) => c!.value);
  }, [organizationTeams, respondent]);

  const isEmployeeManagementDisabled = useFeatureFlag(Features.DisableEmployeeManagement);

  useEffect(() => {
    if (isEmpty(processSurveysToAdd) && processSurveys != null) {
      setProcessSurveysToAdd(
        processSurveys.reduce(
          (acc, sg) => ({
            ...acc,
            [sg.id]: { surveyGroupId: sg.id, addedOn: '', checked: false },
          }),
          {},
        ) || {},
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processSurveys]);

  const [onboardingSurveysToAdd, setOnboardingSurveysToAdd] = useState<SurveyGroupEntries>({});

  useEffect(() => {
    if (isEmpty(onboardingSurveysToAdd) && onboardings != null && onboardings?.length > 0) {
      setOnboardingSurveysToAdd(
        onboardings.reduce(
          (acc, sg, index) => ({
            ...acc,
            [sg.id]: { surveyGroupId: sg.id, addedOn: '', checked: index === 0 },
          }),
          {},
        ) || {},
      );
    }
  }, [onboardingSurveysToAdd, onboardings]);

  const [onboardingOngoingSurveys, setOnboardingOngoingSurveys] = useState<
    Pick<SurveyGroupSurveysData, 'id' | 'topic'>[]
  >([]);
  const [verifyPhoneNumbers] = useVerifyPhoneNumbersMutation();
  const formikRef = useRef<any>(null);
  const RespondentFormValidationSchema = useMemo(
    () =>
      createValidationSchema(
        t,
        otherRespondentEmails,
        otherRespondentPhoneNumbers,
        onboardingSurveysToAdd,
        processSurveysToAdd,
      ),
    [t, otherRespondentEmails, otherRespondentPhoneNumbers, onboardingSurveysToAdd, processSurveysToAdd],
  );

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current?.validateForm();
    }
  }, [onboardingSurveysToAdd, processSurveysToAdd]);

  const [deleteRespondent] = useDeleteRespondentMutation({
    refetchQueries: [getOrganizationTeams],
    awaitRefetchQueries: true,
  });

  const structuredTeams = useMemo(
    () =>
      constructTeamStructure(organizationTeams, t, (team) => ({
        ...team,
        name: team.name || t('teams:withoutLeader'),
      })),
    [organizationTeams, t],
  );

  const getContacts = (values: FormikValues) => {
    const contacts: ContactInput[] = [];
    if ((workEmail || values.primaryEmail) && workEmail !== values.primaryEmail) {
      contacts.push({
        source: ContactSource.Primary,
        contactType: ContactType.Email,
        value: values.primaryEmail,
      });
    }
    if ((personalEmail || values.secondaryEmail) && personalEmail !== values.secondaryEmail) {
      contacts.push({
        source: ContactSource.Respondent,
        contactType: ContactType.Email,
        value: values.secondaryEmail || null,
      });
    }
    if ((phone || values.phone) && (phone !== values.phone || countryCallingCode !== values.callingCode)) {
      contacts.push({
        source: ContactSource.Respondent,
        contactType: ContactType.Sms,
        value: values.phone ? createPhoneNumberWithCallingCode(values.phone, values.callingCode) : null,
      });
    }

    return contacts;
  };

  const createAddInput = (values: FormikValues, runOngoingSteps?: string[]) => {
    const newInput: RespondentInput = {
      firstname: values.firstname.trim(),
      surname: values.surname.trim(),
      disabled: values.disabled,
      preferredContactType: values.preferredContactType,
      reportTeamIds: [
        ...new Set([
          ...getRootTeamsOnly(
            values.reportTeamIds.filter((id: string) => id !== SELECT_ALL_ID),
            structuredTeams,
          ),
          ...values.notificationTeamIds.filter((id: string) => id !== SELECT_ALL_ID),
        ]),
      ],
      notificationTeamIds: values.notificationTeamIds.filter((id: string) => id !== SELECT_ALL_ID),
    };

    newInput.organizationId = organization.id;

    newInput.language = values.language === 'unset' || organization.languages.length <= 1 ? null : values.language;

    newInput.teamId = values.teamId === NO_SUPERVISOR ? null : (newInput.teamId = values.teamId);

    if (values.primaryEmail) {
      newInput.email = values.primaryEmail;
    }

    const contacts = getContacts(values);

    if (contacts.length > 0) {
      newInput.contacts = contacts;
    }
    newInput.surveyGroups = [];
    if (!values.disabled && values.addToSurveys && values.addToOnboarding) {
      newInput.surveyGroups.push(
        ...Object.values(onboardingSurveysToAdd)
          .filter((sg) => sg.checked)
          .map(({ checked, ...sg }) => ({ ...sg, runOngoingSteps })),
      );
    }
    if (!values.disabled && values.addToSurveys) {
      newInput.surveyGroups.push(
        ...Object.values(processSurveysToAdd)
          .filter((sg) => sg.checked)
          .map(({ checked, ...sg }) => sg),
      );
    }
    if (!values.disabled && values.addToSurveys && !!standaloneSurveysToAdd.length) {
      newInput.surveyIds = standaloneSurveysToAdd;
    }

    return newInput;
  };

  const createUpdateInput = (values: FormikValues) => {
    const updateInput: UpdateRespondentInput = {};

    if (respondent) {
      Object.keys(omit(['language'], values)).forEach((key) => {
        if (
          (respondent[key as keyof Member] || respondent[key as keyof Member] === '' || key === 'disabled') &&
          respondent[key as keyof Member] !== values[key]
        ) {
          const value = values[key];
          updateInput[key as keyof UpdateRespondentInput] = typeof value === 'string' ? value.trim() : value;
        }
      });

      // Filter out permanently enabled team ids
      updateInput.reportTeamIds = [
        ...new Set([
          ...getRootTeamsOnly(
            values.reportTeamIds.filter((id: string) => id !== SELECT_ALL_ID),
            structuredTeams,
          ),
          ...values.notificationTeamIds.filter((id: string) => id !== SELECT_ALL_ID),
        ]),
      ];

      updateInput.notificationTeamIds = values.notificationTeamIds.filter((id: string) => id !== SELECT_ALL_ID);

      if (values.language === 'unset' && respondent.language) {
        updateInput.language = null;
      } else if (
        values.language !== 'unset' &&
        (!respondent.language || values.language !== respondent.language.code)
      ) {
        updateInput.language = values.language;
      }

      if (values.teamId !== NO_SUPERVISOR && (!team || values.teamId !== team.id)) {
        updateInput.teamId = values.teamId;
      } else if (values.teamId === NO_SUPERVISOR && team) {
        updateInput.teamId = null;
      }

      const contacts = getContacts(values);

      if (contacts.length > 0) {
        updateInput.contacts = contacts;
      }

      return updateInput;
    }
  };

  const handleAddRespondentToOnboardingOngoingSurveys = (
    values: FormikValues,
    { setSubmitting }: FormikHelpers<any>,
  ) => {
    return async (runOngoingSteps: string[], addedOn: Date) => {
      try {
        setSubmitting(true);
        if (!respondent || !respondent.id) {
          await (saveRespondent as CreateRespondentMutationFn)({
            variables: {
              input: createAddInput(values, runOngoingSteps),
            },
          });
        }
      } catch (e) {
        setSubmitting(false);
      }
    };
  };

  const handleSaveRespondent = () => async (values: FormikValues, formikHelpers: FormikHelpers<any>) => {
    if (
      !respondent &&
      values.addToSurveys &&
      values.addToOnboarding &&
      onboardingOngoingSurveys.length &&
      !values.disabled
    ) {
      ee.emitAddSGRespondentToOngoingSurveys(
        handleAddRespondentToOnboardingOngoingSurveys(values, formikHelpers),
        new Date(Object.values(onboardingSurveysToAdd).filter((sg) => sg.checked)[0].addedOn),
        onboardingOngoingSurveys,
      );
    } else {
      const { setSubmitting } = formikHelpers;
      try {
        setSubmitting(true);
        setUpdatedReportTeamIds?.(values.reportTeamIds ?? []);
        if (!respondent || !respondent.id) {
          const addInput = createAddInput(values);
          await (saveRespondent as CreateRespondentMutationFn)({
            variables: {
              input: addInput,
            },
          });
        }

        const updatedInput = createUpdateInput(values);

        if (respondent && respondent.id && updatedInput) {
          await (saveRespondent as UpdateRespondentMutationFn)({
            variables: {
              id: respondent.id,
              input: updatedInput,
            },
          });
        }
        setSubmitting(false);
      } catch (e) {
        setSubmitting(false);
      }
    }
  };

  const teams = getTeams(organizationTeams);

  const createdAt = respondent?.createdAt && format(new Date(respondent.createdAt), 'Pp');
  const imported: Record<string, { icon: string; tooltipText: string; text: string }> = {
    TEAMIO: {
      icon: TeamioIcon,
      tooltipText: t('addedAtTooltip', {
        date: createdAt ?? '',
      }),
      text: t('addedByTeamio'),
    },
    AUTOMATIC_IMPORT: {
      icon: ExternalPerson,
      tooltipText: t('addedAtTooltip', {
        date: createdAt ?? '',
      }),
      text: t('automaticImported'),
    },
  };

  const reportTeamIds = useMemo(() => {
    return (
      (respondent &&
        reportRecipients
          ?.filter((recipient) => recipient.respondent?.id === respondent.id && !recipient.teamDeleted)
          .map((recipient) => recipient.team?.id || SELECT_ALL_ID)) ||
      []
    );
  }, [reportRecipients, respondent]);

  const initialReportTeamIds = useMemo(() => {
    const hasLegacyAllTeamsFlag = reportTeamIds.includes(SELECT_ALL_ID);
    const realReportTeamIds = reportTeamIds.filter((id) => id !== SELECT_ALL_ID);
    const currentIds = [...new Set([...(respondent?.leaderOfTeams.map((t) => t.id) ?? []), ...realReportTeamIds])];
    const rootTeamIds = structuredTeams.map((t) => t.id);
    const allRootsSelected =
      hasLegacyAllTeamsFlag || (rootTeamIds.length > 0 && rootTeamIds.every((id) => currentIds.includes(id)));

    if (allRootsSelected) {
      return [...new Set([...currentIds, ...rootTeamIds, SELECT_ALL_ID])];
    }
    return currentIds;
  }, [respondent, reportTeamIds, structuredTeams]);

  const initialNotificationTeamIds = useMemo(() => {
    const notificationTeamIds = respondent?.notificationTeamIds ?? [];
    const availableTeams = getAvailableNotificationTeams(initialReportTeamIds, structuredTeams);
    const availableIds = availableTeams.map((t) => t.id);

    const allSelected = availableIds.length > 0 && availableIds.every((id) => notificationTeamIds.includes(id));

    if (allSelected) {
      return [...new Set([...notificationTeamIds, SELECT_ALL_ID])];
    }
    return notificationTeamIds;
  }, [respondent, initialReportTeamIds, structuredTeams]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        firstname: respondent && respondent.firstname,
        surname: respondent && respondent.surname,
        primaryEmail: workEmail || '',
        secondaryEmail: personalEmail || '',
        phone: phone || '',
        callingCode: countryCallingCode || '',
        preferredContactType: (respondent && respondent.preferredContactType) || ContactType.Email,
        teamId: team ? (team.structureId !== '' ? team.id : NO_SUPERVISOR) : null,
        language: (respondent && respondent.language && respondent.language.code) || 'unset',
        disabled: respondent && respondent.disabled,
        addToSurveys: false,
        addToOnboarding: false,
        reportTeamIds: initialReportTeamIds,
        notificationTeamIds: initialNotificationTeamIds,
        addToProcessSurveys: false,
      }}
      validationSchema={RespondentFormValidationSchema}
      onSubmit={handleSaveRespondent()}
    >
      {({
        values,
        errors,
        touched,
        handleSubmit,
        handleChange,
        handleBlur,
        setFieldValue,
        isSubmitting,
        setValues,
        initialValues,
      }) => {
        const newFormStatus = {
          somethingChanged: !equals(values, initialValues),
          save: handleSubmit,
          saveDisabled:
            !isEmpty(errors) ||
            isSubmitting ||
            unverifiedPhoneNumber ||
            (values.addToSurveys && Object.values(processSurveysToAdd).some((sg) => sg.checked && !sg.addedOn)) ||
            (values.addToSurveys &&
              values.addToOnboarding &&
              Object.values(onboardingSurveysToAdd).some((sg) => sg.checked && !sg.addedOn)),
        };
        if (!equals(newFormStatus, formStatus)) {
          setFormStatus?.(newFormStatus);
        }

        const wasContactTouched = touched.primaryEmail || touched.secondaryEmail || touched.phone;

        const handleReportTeamsChange = (newReportTeamIds: string[]) => {
          const mandatoryIds = respondent?.leaderOfTeams.map((t) => t.id) ?? [];
          const appliedIds = applyTeamSelectionSpecialLogic(
            newReportTeamIds,
            values.reportTeamIds,
            structuredTeams,
            mandatoryIds,
          );
          setFieldValue('reportTeamIds', appliedIds);

          const validNotificationIds = values.notificationTeamIds.filter(
            (id: string) => id !== SELECT_ALL_ID && appliedIds.includes(id),
          );

          const availableTeams = getAvailableNotificationTeams(
            appliedIds.filter((id: string) => id !== SELECT_ALL_ID),
            structuredTeams,
          );
          const availableIds = availableTeams.map((t) => t.id);

          const allSelected = availableIds.length > 0 && availableIds.every((id) => validNotificationIds.includes(id));

          const finalNotificationIds = allSelected ? [...validNotificationIds, SELECT_ALL_ID] : validNotificationIds;

          setFieldValue('notificationTeamIds', finalNotificationIds);
        };

        const handleNotificationChange = (newIds: string[]) => {
          const availableTeams = getAvailableNotificationTeams(values.reportTeamIds as string[], structuredTeams);
          const availableIds = availableTeams.map((t) => t.id);

          const wasSelectAllSelected = values.notificationTeamIds.includes(SELECT_ALL_ID);
          const isSelectAllSelected = newIds.includes(SELECT_ALL_ID);

          let finalIds: string[];

          if (!wasSelectAllSelected && isSelectAllSelected) {
            finalIds = [...availableIds, SELECT_ALL_ID];
          } else if (wasSelectAllSelected && !isSelectAllSelected) {
            const realSelectedIds = newIds.filter((id) => id !== SELECT_ALL_ID);

            const areAllPresent = availableIds.length > 0 && availableIds.every((id) => realSelectedIds.includes(id));

            if (areAllPresent) {
              finalIds = [];
            } else {
              finalIds = realSelectedIds;
            }
          } else {
            const realSelectedIds = newIds.filter((id) => id !== SELECT_ALL_ID);

            const allSelected = availableIds.length > 0 && availableIds.every((id) => realSelectedIds.includes(id));

            if (allSelected) {
              finalIds = [...realSelectedIds, SELECT_ALL_ID];
            } else {
              finalIds = realSelectedIds;
            }
          }

          setFieldValue('notificationTeamIds', finalIds);
        };

        const realNotificationTeamIds = values.notificationTeamIds.filter((id: string) => id !== SELECT_ALL_ID);
        const notificationTeamsCount = realNotificationTeamIds.length;

        const isCreditsOrg = organization.productVersion?.product === ProductCode.Credits;
        const onboardingCredits = values.addToOnboarding
          ? Object.values(onboardingSurveysToAdd).filter((sg) => sg.checked).length *
            (getCreditPriceForSurveyGroup(onboardings?.[0]?.topicGroup?.typeCode) ?? 0)
          : 0;
        const processCredits =
          processSurveys
            ?.filter((sg) => processSurveysToAdd[sg.id]?.checked)
            .reduce((sum, sg) => sum + (getCreditPriceForSurveyGroup(sg.topicGroup?.typeCode) ?? 0), 0) ?? 0;
        const standaloneCredits = standaloneSurveysToAdd.reduce((sum, surveyId) => {
          const survey = surveys?.find((s) => s.id === surveyId);
          const creditPrice = isPulserTopicGroup(survey?.topicGroup)
            ? ONE_TIME_PULSER_SURVEY_CREDIT_PRICE
            : ONE_TIME_SURVEY_CREDIT_PRICE;
          return sum + creditPrice;
        }, 0);
        const totalCredits = onboardingCredits + processCredits + standaloneCredits;

        return (
          <Form onSubmit={handleSubmit} data-icom={'form-respondent'}>
            <HeadingLarge className="m-0 mb-8">
              {respondent ? `${respondent.firstname} ${respondent.surname}` : t('addRespondent:addEmployee')}
            </HeadingLarge>
            <Card className="p-8">
              <Card.Body className="p-0">
                <HeadingMedium className="m-0 mb-7">{t('basicInformation')}</HeadingMedium>
                {isEmployeeManagementDisabled && <InfoMessage>{t('employeesUpdateInfoDescription')}</InfoMessage>}

                <Row>
                  <FormGroup as={Col} sm={6}>
                    <FormGroupLabel>
                      {t('teamsScreen:name')}
                      <RedAsterisk />
                    </FormGroupLabel>
                    <FormControl
                      disabled={isEmployeeManagementDisabled}
                      type="text"
                      name="firstname"
                      value={values.firstname || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.firstname && !!errors.firstname}
                    />
                    <FormErrorFeedback error={errors.firstname} />
                    {respondent?.originator && imported[respondent.originator] && (
                      <div className="mt-4">
                        <Tooltip title={imported[respondent.originator].tooltipText}>
                          <img src={imported[respondent.originator].icon} alt="Teamio" className="mr-4" />
                        </Tooltip>
                        <FormLabelStyled>{imported[respondent.originator].text}</FormLabelStyled>
                      </div>
                    )}
                  </FormGroup>
                  <FormGroup as={Col} sm={6}>
                    <FormGroupLabel className="form-group__label">
                      {t('teamsScreen:surname')}
                      <RedAsterisk />
                    </FormGroupLabel>
                    <FormControl
                      disabled={isEmployeeManagementDisabled}
                      type="text"
                      name="surname"
                      value={values.surname || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.surname && !!errors.surname}
                    />
                    <FormErrorFeedback error={errors.surname} />
                  </FormGroup>
                </Row>
                <Row>
                  <FormGroup as={Col} sm={12}>
                    <FormGroupLabel>{t('teamsScreen:workEmail')}</FormGroupLabel>
                    <FormControl
                      disabled={isEmployeeManagementDisabled}
                      type="text"
                      name="primaryEmail"
                      value={values.primaryEmail || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={wasContactTouched && !!errors.primaryEmail}
                    />
                    <FormErrorFeedback error={errors.primaryEmail} />
                  </FormGroup>
                </Row>
                <Row>
                  <FormGroup as={Col} sm={12}>
                    <FormGroupLabel>{t('teamsScreen:personalEmail')}</FormGroupLabel>
                    <FormControl
                      type="email"
                      name="secondaryEmail"
                      value={values.secondaryEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={wasContactTouched && !!errors.secondaryEmail}
                    />
                    <FormErrorFeedback error={errors.secondaryEmail} />
                  </FormGroup>
                </Row>
                <Row>
                  <FormGroup as={Col} sm={6}>
                    <FormGroupLabel>{t('callingCode')}</FormGroupLabel>
                    <CallingCodeInput
                      callingCode={values.callingCode || ''}
                      onChange={(callingCode: string) => {
                        setValues(() => ({ ...values, callingCode }));
                      }}
                      isInvalid={!!errors.callingCode}
                    />
                    <FormErrorFeedback error={errors.callingCode} />
                  </FormGroup>
                  <FormGroup as={Col} sm={6}>
                    <FormGroupLabel>{t('teamsScreen:phone')}</FormGroupLabel>
                    <FormControl
                      type="tel"
                      name="phone"
                      value={values.phone}
                      onChange={handlePhoneChange(handleChange)}
                      onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handlePhoneBlur(values.callingCode, e.target.value, (internationalPhoneNumber: string) =>
                          verifyPhoneNumbers({
                            variables: { phoneNumbers: [internationalPhoneNumber] },
                            onCompleted: (data) => {
                              setUnverifiedPhoneNumber(
                                !data.verifyPhoneNumbers.find((pn) => pn.phoneNumber === internationalPhoneNumber),
                              );
                            },
                            onError: () => {
                              setUnverifiedPhoneNumber(false);
                            },
                          }),
                        )
                      }
                      isInvalid={wasContactTouched && (!!errors.phone || unverifiedPhoneNumber)}
                    />
                    <FormErrorFeedback error={errors.phone || (unverifiedPhoneNumber && t('enterValidPhone'))} />
                  </FormGroup>
                </Row>
                <Row className="mt-4">
                  <FormGroup as={Col} sm={6}>
                    <FormGroupLabel className="ml-0">{t('sendSurveyTo')}</FormGroupLabel>
                  </FormGroup>
                  <FormGroup as={Col} sm={3}>
                    <FormRadioInput
                      name="preferredContactType"
                      value={ContactType.Email}
                      clicked={() => {
                        setFieldValue('preferredContactType', ContactType.Email);
                      }}
                      changed={handleChange}
                      onBlur={handleBlur}
                      defaultChecked={values.preferredContactType === ContactType.Email}
                      label={t('teamsScreen:email')}
                      disabled={!values.primaryEmail && !values.secondaryEmail}
                    />
                  </FormGroup>
                  <FormGroup as={Col} sm={3}>
                    <FormRadioInput
                      name="preferredContactType"
                      value={ContactType.Sms}
                      clicked={() => {
                        setFieldValue('preferredContactType', ContactType.Sms);
                      }}
                      changed={handleChange}
                      onBlur={handleBlur}
                      defaultChecked={values.preferredContactType === ContactType.Sms}
                      label={t('teamsScreen:phone')}
                      disabled={!values.phone}
                    />
                  </FormGroup>
                  <RadioButtonError>
                    <FormErrorFeedback error={errors.preferredContactType} />
                  </RadioButtonError>
                </Row>
                {organization.languages.length > 1 && (
                  <Row>
                    <FormGroup as={Col} sm={12}>
                      <FormGroupLabel>{t('settings:language')}</FormGroupLabel>
                      <Field
                        component={FormSelect}
                        name="language"
                        options={[
                          { value: 'unset', label: t('surveyLanguage') },
                          ...sortLanguages(
                            organization.languages,
                            new Map(organization.languages.map((l) => [l.code, t(`languageSelector:${l.code}`)])),
                          ).map((language) => ({
                            value: language!.code,
                            label: t(`languageSelector:${language!.code}`),
                          })),
                        ]}
                        defaultValue={values.language}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </FormGroup>
                  </Row>
                )}
                <Row>
                  <FormGroup as={Col} sm={12}>
                    <ToggleContainerWithText>
                      <div>
                        <BodyMedium margin={`0 0 ${theme.spacing.d} 0`}>{t(`surveyParticipant`)}</BodyMedium>
                        <BodySmall color={`${theme.colors.text.secondary}`}>
                          {t(`surveyParticipantDescription`)}
                        </BodySmall>
                      </div>
                      <ToggleContainer>
                        <ToggleButton
                          dataCy="disabled-toggle"
                          value={!values.disabled}
                          onChange={(disabled: boolean) => {
                            setValues(() => ({ ...values, disabled: !disabled }));
                          }}
                        />
                      </ToggleContainer>
                    </ToggleContainerWithText>
                    {!values.disabled &&
                      (!!onboardings?.length || !!processSurveys?.length || !!surveys?.length) &&
                      !respondent && (
                        <ToggleContainerWithText>
                          <div>
                            <BodyMedium margin={`0 0 ${theme.spacing.d} 0`}>{t(`addToOngoingPlanned`)}</BodyMedium>
                            <BodySmall color={`${theme.colors.text.secondary}`}>
                              {t(`addToOngoingPlannedDescription`)}
                            </BodySmall>
                          </div>
                          <ToggleContainer>
                            <ToggleButton
                              dataCy="add-to-survey-toggle"
                              value={values.addToSurveys}
                              onChange={(addToSurveys: boolean) => {
                                setValues(() => ({ ...values, addToSurveys }));
                              }}
                            />
                          </ToggleContainer>
                        </ToggleContainerWithText>
                      )}
                  </FormGroup>
                </Row>
                {!respondent &&
                  !values.disabled &&
                  (!!onboardings?.length || !!surveys?.length || !!processSurveys?.length) &&
                  values.addToSurveys && (
                    <Row>
                      <FormGroup as={Col} sm={12} className="pl-0 pr-8">
                        <FormGroup className="form-check-group ml-0">
                          {values.addToSurveys && (
                            <>
                              <div className="ml-6">
                                {!!onboardings?.length && (
                                  <>
                                    <FormGroup>
                                      <Checkbox
                                        selected={values.addToOnboarding}
                                        onToggle={(e) => {
                                          setValues(() => ({ ...values, addToOnboarding: !values.addToOnboarding }));
                                        }}
                                        text={t('addToOnboarding')}
                                        dataCy="add-to-onboarding-input"
                                      />
                                    </FormGroup>
                                    {values.addToOnboarding && (
                                      <OnboardingSelect
                                        productHistory={organization.productVersionHistory}
                                        onboardings={onboardings}
                                        onboardingSurveysToAdd={onboardingSurveysToAdd}
                                        setOnboardingSurveysToAdd={setOnboardingSurveysToAdd}
                                        setOnboardingOngoingSurveys={setOnboardingOngoingSurveys}
                                        error={errors.addToOnboarding}
                                      />
                                    )}
                                  </>
                                )}
                              </div>
                              {!!surveys?.length && (
                                <StandaloneSurveySelect
                                  surveys={surveys}
                                  standaloneSurveysToAdd={standaloneSurveysToAdd}
                                  setStandaloneSurveysToAdd={setStandaloneSurveysToAdd}
                                />
                              )}
                              {!!processSurveys?.length && (
                                <ProcessSurveySelect
                                  processSurveys={processSurveys}
                                  processSurveysToAdd={processSurveysToAdd}
                                  setProcessSurveysToAdd={setProcessSurveysToAdd}
                                  error={errors.addToProcessSurveys}
                                />
                              )}
                              {isCreditsOrg && totalCredits > 0 && (
                                <div className="ml-6 mt-4">
                                  <BodyMedium style={{ fontWeight: 'bold' }}>
                                    {t('organizationImportModals:totalCredits', { count: totalCredits })}
                                  </BodyMedium>
                                </div>
                              )}
                            </>
                          )}
                        </FormGroup>
                      </FormGroup>
                    </Row>
                  )}
                <Row>
                  <TeamLeaderField
                    handleChange={handleChange}
                    disableCreating
                    handleBlur={handleBlur}
                    teams={teams}
                    values={values}
                    setFieldValue={setFieldValue}
                    error={touched.teamId && errors.teamId}
                    fullWidth
                    disabled={isEmployeeManagementDisabled}
                    createTeamOption={!disableCreatingTeam}
                    onCreateTeamSelected={onCreateTeamSelected}
                  />
                </Row>
              </Card.Body>
            </Card>
            <ReportsSection
              firstname={values.firstname}
              surname={values.surname}
              structuredTeams={structuredTeams}
              reportTeamIds={values.reportTeamIds as string[]}
              respondent={respondent}
              onChange={handleReportTeamsChange}
              onBlur={handleBlur}
            />

            <Card className="p-8">
              <Card.Body className="p-0" data-cy="notifications-section">
                <HeadingMedium className="m-0 mb-7">{t('NotificationsSection')}</HeadingMedium>
                <BodyMedium className="m-0 mb-7" color={theme.colors.text.secondary}>
                  <Trans
                    t={t}
                    i18nKey="NotificationsSectionDescription"
                    count={notificationTeamsCount}
                    values={{
                      name: `${values.firstname} ${values.surname}`,
                      postProcess: 'interval',
                      count: notificationTeamsCount,
                    }}
                    components={[<span className="font-weight-bold" />]}
                  />
                </BodyMedium>
                <Field
                  component={SupervisorTeamSelect}
                  isDisabled={values.reportTeamIds.length === 0}
                  name="notificationTeamIds"
                  teams={[
                    {
                      id: SELECT_ALL_ID,
                      name: t('addRespondent:allSelectedInSectionReports'),
                      subTeams: getAvailableNotificationTeams(values.reportTeamIds as string[], structuredTeams),
                    },
                  ]}
                  value={values.notificationTeamIds}
                  onChange={handleNotificationChange}
                  onBlur={handleBlur}
                  multiselect={true}
                  filterMultiValueSelectedIndicators={(id: string) => {
                    // if all teams are selected, we should not show the "all teams" option
                    if (values.notificationTeamIds.includes(SELECT_ALL_ID)) {
                      return id === SELECT_ALL_ID;
                    }
                    return true;
                  }}
                />
              </Card.Body>
            </Card>

            {respondent && !isEmployeeManagementDisabled && (
              <Card className="p-8">
                <Card.Body className="p-0">
                  <HeadingMedium className="m-0 mb-7">{t('deletion')}</HeadingMedium>
                  <DangerZoneWarning
                    heading={t('deletionWarningTitle', { fullName: `${values.firstname} ${values.surname}` })}
                    descriptionPoints={t('deletionWarningDescription').split('\n')}
                  />
                  <SubtleButton
                    style={{ marginTop: '32px' }}
                    onClick={(e) => {
                      e.preventDefault();
                      ee.emitModalDeleteRespondent(
                        `${respondent.firstname} ${respondent.surname}`,
                        values.primaryEmail,
                        async () => {
                          await deleteRespondent({
                            variables: {
                              respondentId: respondent.id,
                            },
                          });
                          addToast(
                            t('teamsScreen:respondentDeleted', {
                              name: `${respondent.firstname} ${respondent.surname}`,
                            }),
                          );
                          onDeletionComplete?.();
                        },
                        undefined,
                      );
                    }}
                    data-cy="delete-respondent-button"
                    color="danger"
                  >
                    {t('delete')}
                  </SubtleButton>
                </Card.Body>
              </Card>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default RespondentForm;

const RedAsterisk = () => <span style={{ color: theme.colors.emotionDanger.default }}>*</span>;

export const DangerZoneWarning = ({
  heading,
  text,
  descriptionPoints,
}: {
  heading?: string;
  text?: string;
  descriptionPoints?: string[];
}) => (
  <StyledDangerZoneWarning>
    <DeprecatedErrorWarningIcon />
    <div>
      {heading && <div className="dangerZoneHeading">{heading}</div>}
      {text && <div className="dangerZoneText">{text}</div>}
      {descriptionPoints && (
        <ul>
          {descriptionPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      )}
    </div>
  </StyledDangerZoneWarning>
);

const StyledDangerZoneWarning = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: ${theme.spacing.f};
  padding: ${theme.spacing.f};
  border: 1px solid ${theme.colors.emotionDanger.default};
  border-radius: ${theme.borderRadius.l};
  background: ${theme.colors.emotionDanger.background};
  color: ${theme.colors.emotionDanger.default};
  svg > path {
    fill: ${theme.colors.emotionDanger.default};
  }

  & > div {
    margin-left: ${theme.spacing.f};
  }

  ul {
    margin: 0;
    padding-left: ${theme.spacing.f};
  }

  .dangerZoneHeading {
    font-weight: ${theme.typography.body.medium.bold.fontWeight};
    margin-bottom: ${theme.spacing.c};
  }
  .dangerZoneText {
    margin-top: ${theme.spacing.b};
  }
`;

export const InfoMessage = styled.div`
  margin-top: ${theme.spacing.f};
  padding: ${theme.spacing.f};
  margin-bottom: ${theme.spacing.g};
  border: 1px solid ${theme.colors.emotionInformative.default};
  border-radius: ${theme.borderRadius.l};
  background: ${theme.colors.emotionInformative.background};
  color: ${theme.colors.emotionInformative.default};
`;

const getAllSubTeamIds = (team: TeamWithSubTeams): string[] => {
  let ids: string[] = [];
  if (team.subTeams) {
    team.subTeams.forEach((subTeam) => {
      ids.push(subTeam.id);
      ids = ids.concat(getAllSubTeamIds(subTeam));
    });
  }
  return ids;
};

const getAllTeamIdsInStructure = (teams: TeamWithSubTeams[]): string[] => {
  let ids: string[] = [];
  teams.forEach((team) => {
    ids.push(team.id);
    ids = ids.concat(getAllTeamIdsInStructure(team.subTeams));
  });
  return ids;
};

const getFullStructureIds = (rootIds: string[], allTeams: TeamWithSubTeams[]): string[] => {
  let ids: string[] = [];
  rootIds.forEach((id) => {
    ids.push(id);
    const team = findTeamById(allTeams, id);
    if (team) {
      ids = ids.concat(getAllSubTeamIds(team));
    }
  });
  return [...new Set(ids)];
};

const applyTeamSelectionSpecialLogic = (
  newIds: string[],
  currentIds: string[],
  allTeams: TeamWithSubTeams[],
  mandatoryIds: string[] = [],
): string[] => {
  const wasSelectAll = currentIds.includes(SELECT_ALL_ID);
  const isSelectAll = newIds.includes(SELECT_ALL_ID);

  if (!wasSelectAll && isSelectAll) {
    return [...getAllTeamIdsInStructure(allTeams), SELECT_ALL_ID];
  }

  if (wasSelectAll && !isSelectAll) {
    const removed = currentIds.filter((id) => !newIds.includes(id));

    if (removed.length === 1 && removed[0] === SELECT_ALL_ID) {
      return getFullStructureIds(mandatoryIds, allTeams);
    }
  }

  const added = newIds.filter((id) => !currentIds.includes(id));
  const removed = currentIds.filter((id) => !newIds.includes(id));

  let result = [...currentIds].filter((id) => id !== SELECT_ALL_ID);

  added.forEach((id) => {
    if (id === SELECT_ALL_ID) return;
    result.push(id);
    const team = findTeamById(allTeams, id);
    if (team) {
      const subs = getAllSubTeamIds(team);
      result = [...result, ...subs];
    }
  });

  removed.forEach((id) => {
    if (id === SELECT_ALL_ID) return;

    result = result.filter((rid) => rid !== id);

    const team = findTeamById(allTeams, id);
    if (team) {
      const subs = getAllSubTeamIds(team);
      result = result.filter((rid) => !subs.includes(rid));
    }
  });

  result = [...new Set(result)];

  const allIds = getAllTeamIdsInStructure(allTeams);
  const isFullSelection = allIds.every((id) => result.includes(id));

  if (isFullSelection && result.length > 0) {
    return [...result, SELECT_ALL_ID];
  }

  return result;
};

const getAvailableNotificationTeams = (reportTeamIds: string[], allTeams: TeamWithSubTeams[]): TeamWithSubTeams[] => {
  const result: TeamWithSubTeams[] = [];
  reportTeamIds.forEach((id) => {
    const team = findTeamById(allTeams, id);
    if (team) {
      result.push(team);
      const subTeamsResult = getAvailableNotificationTeams(getAllSubTeamIds(team), allTeams);
      subTeamsResult.forEach((t) => result.push(t));
    }
  });

  return uniqBy((t: TeamWithSubTeams) => t.id, result)
    .map((t) => ({ ...t, subTeams: [] }))
    .sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
};

// 1. when all teams are selected, we should show only "all teams" in the dropdown preview
// 2. dropdown preview should not have max-height 200px and scrolbar - LongTeamSelectScrollWrapper
// 3. there should be some prop on the team select that would let you specify how many items should render in the dropdown preview, if there are
//         more than X items, the dropdown preview should show another item "+ x more teams". When clicked, the dropdown should show all teams.
// 4. dropdown and X icon move to the top right corner of the dropdown.
// 5. Boleslav Kratochvíl má oprávnění vidět reporty pro 5 týmů a pro 15 týmů které jsou těmto týmům podřízené. - předělat textaci

// šedá ikona

// dát všechny týmy i pro notifikace - Všechny vybrané v sekci reportů
